import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../config';
import Footer from '../components/Footer';

export default function VerifyEmailChangePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [emailInfo, setEmailInfo] = useState({ oldEmail: '', newEmail: '' });

  useEffect(() => {
    let isCancelled = false;

    const verifyEmailChange = async () => {
      try {
        const response = await fetch(`${API_URL}/auth/verify-email-change/${token}`);
        const data = await response.json();

        if (isCancelled) return; // Don't update state if component unmounted

        if (response.ok) {
          setStatus('success');
          setMessage(data.message);
          setEmailInfo({
            oldEmail: data.old_email,
            newEmail: data.new_email
          });

          // Redirect to login after 5 seconds
          setTimeout(() => {
            if (!isCancelled) {
              navigate('/login', {
                state: {
                  message: 'Email cambiata con successo! Accedi con la tua nuova email.'
                }
              });
            }
          }, 5000);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Errore durante la verifica del cambio email');
        }
      } catch (error) {
        if (!isCancelled) {
          setStatus('error');
          setMessage('Errore di connessione. Riprova più tardi.');
        }
      }
    };

    if (token && status === 'verifying') {
      verifyEmailChange();
    }

    return () => {
      isCancelled = true;
    };
  }, [token, navigate, status]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col px-4">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-xl p-8">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#0E60C6' }}></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifica in corso...</h2>
              <p className="text-gray-600">Stiamo verificando il cambio di email</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#10B981' }}>
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Email Cambiata!</h2>
              <p className="text-gray-600 mb-6">{message}</p>

              {emailInfo.oldEmail && emailInfo.newEmail && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>Email precedente:</strong> {emailInfo.oldEmail}
                  </p>
                  <p className="text-sm text-blue-800">
                    <strong>Nuova email:</strong> {emailInfo.newEmail}
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-500 mb-4">
                Sarai reindirizzato alla pagina di login tra qualche secondo...
              </p>

              <Link
                to="/login"
                className="inline-block px-6 py-3 text-white rounded-lg transition font-medium"
                style={{ backgroundColor: '#0E60C6' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0B4FA3'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0E60C6'}
              >
                Vai al Login
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Verifica Fallita</h2>
              <p className="text-gray-600 mb-6">{message}</p>

              <div className="space-y-3">
                <Link
                  to="/my-account"
                  className="block px-6 py-3 text-white rounded-lg transition font-medium"
                  style={{ backgroundColor: '#0E60C6' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0B4FA3'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0E60C6'}
                >
                  Torna al Mio Account
                </Link>
                <Link
                  to="/"
                  className="block px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Torna alla Home
                </Link>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
